/**
 * Event Bubble Plugin for Nuxt 3
 *
 * Provides $bubble functionality to emit events that bubble up through parent components.
 *
 * Usage:
 *
 * 1. Options API:
 *    ```vue
 *    <script lang="ts">
 *    export default {
 *      // Optional: declare emits for better type safety and documentation
 *      emits: ['some-event'],
 *
 *      methods: {
 *        someMethod() {
 *          this.$bubble('some-event', payload)
 *        }
 *      }
 *    }
 *    </script>
 *    ```
 *
 * 2. Composition API:
 *    ```vue
 *    <script setup lang="ts">
 *    const { $bubbleEvent } = useNuxtApp()
 *
 *    // Optional: declare emits for better type safety and documentation
 *    defineEmits<{
 *      'some-event': [payload: any]
 *    }>()
 *
 *    const handleSomething = () => {
 *      $bubbleEvent('some-event', payload)
 *    }
 *    </script>
 *    ```
 *
 * Note: While Vue 3 typically requires defineEmits, bubbling will work without it.
 * However, declaring emits is recommended for better code documentation and type safety.
 */
import { defineNuxtPlugin } from 'nuxt/app'
import { getCurrentInstance } from 'vue'

// Augment Vue types
declare module '@vue/runtime-core' {
  export interface ComponentCustomProperties {
    $bubble: (eventName: string, ...args: any[]) => void
  }
}

function bubbleEvent(component: any, eventName: string, args: any[]) {
  if (!component) {
    console.warn(
      `[event-bubble] No component instance found when emitting "${eventName}". Event was not bubbled.`,
    )
    return
  }
  let currentComponent = component.$ || component
  do {
    currentComponent.emit(eventName, ...args)
    currentComponent = currentComponent.$parent || currentComponent.parent
  } while (currentComponent)
}

export default defineNuxtPlugin((nuxtApp) => {
  // For Options API (class components)
  nuxtApp.vueApp.config.globalProperties.$bubble = function (
    eventName: string,
    ...args: any[]
  ) {
    bubbleEvent(this, eventName, args)
  }

  // For Composition API
  return {
    provide: {
      // note it's called bubbleEvent here as it would clash with the $bubble method
      bubbleEvent: (eventName: string, ...args: any[]) => {
        const instance = getCurrentInstance()
        bubbleEvent(instance?.proxy, eventName, args)
      },
    },
  }
})
